<template>
  <div class="dropdown-container flex flex-col p-4">
    <!-- IP Address Input -->
    <BaseText type="label" size="sm" class="text-text-muted">
      IP Address
    </BaseText>
    <input v-model="ipAddress" class="form-input min-w-0 w-full px-2 mt-1" placeholder="eg. 127.0.0.1" />
    <!-- Location Selector -->
    <BaseText type="label" size="sm" class="text-text-muted mt-2.5">
      Country
    </BaseText>
    <div class="w-full mt-1 z-30">
      <BaseSingleDropdown 
        use-parent-width
        :options="locationOptions"
        option-key="code"
        :selected-obj="selectedLocation"
        :selected-label="(opt) => `${opt.name}`"
        :option-label="(opt) => opt.name"
        enable-search
        search-key="name"
        placeholder="Select country"
        search-placeholder="Search countries"
        :max-height="328"
        @change="selectedLocation = $event"
      />
    </div>
    <!-- Endpoint Input -->
    <BaseText type="label" size="sm" class="text-text-muted mt-2.5">
      Endpoint
    </BaseText>
    <input v-model="endpoint" class="form-input min-w-0 w-full px-2 mt-1" placeholder="eg. /discovery/brands" />
    <!-- Response Status Selector -->
    <BaseText type="label" size="sm" class="text-text-muted mt-2.5">
      Response Status
    </BaseText>
    <input v-model="responseStatus" class="form-input min-w-0 w-full px-2 mt-1" placeholder="eg. 200, 404" />
    <!-- Apply Button -->
    <button class="submit-btn w-full px-5 py-1.5 rounded-md mt-4" :disabled="!changesMade"
    :class="{'enabled': changesMade, 'disabled cursor-default': !changesMade}"
    @click="applyChanges">
        <BaseText type="label" size="sm">
          Apply
        </BaseText>
      </button>
  </div>
</template>

<script>
import { getAllCountries } from '../../../utils/countryUtils'

export default {
  name: 'LogsFilterDropdown',
  props: {
    queryFields: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      ipAddress: this.queryFields?.ip_address ?? '',
      endpoint: this.queryFields?.endpoint ?? '',
      responseStatus: this.queryFields?.status_code ?? '',
      locationOptions: [
        { code: null, name: 'Any' },
        ...getAllCountries().sort((a, b) => a.name.localeCompare(b.name))
      ],
      selectedLocation: { code: null, name: 'Any' },

      initQueryFields: { ...this.queryFields }
    }
  },
  created () {
    this.selectedLocation = this.getInitSelectedLocation()
  },
  computed: {
    compiledQueryFields () {
      return {
        ip_address: this.ipAddress?.length ? this.ipAddress : null,
        endpoint: this.endpoint?.length ? this.endpoint : null,
        method: null,
        status_code: this.responseStatus?.length ? this.responseStatus : null,
        country: this.selectedLocation?.code
      }
    },
    changesMade () {
      return JSON.stringify(this.initQueryFields) !== JSON.stringify(this.compiledQueryFields)
    }
  },
  methods: {
    applyChanges () {
      this.$emit('update:queryFields', this.compiledQueryFields)
      this.$emit('close')
    },
    getInitSelectedLocation () {
      if (!this.queryFields?.country) return { code: null, name: 'Any' }
      const country = this.locationOptions.find(opt => opt.code === this.queryFields.country)
      return country ?? { code: null, name: 'Any' }
    }
  }
}
</script>

<style scoped>
.dropdown-container {
  width: 320px;
  border-radius: 14px;
  background-color: white;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}

.form-input {
  color: #06070F;
  border-radius: 8px;
  border: 1px solid;
  border-color: transparent;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: border-color 150ms ease-in-out, box-shadow 150ms ease-in-out;
  padding-top: 5px;
  padding-bottom: 5px;

  /* Body/Small */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
}
.form-input:focus {
  outline: none;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.1), 0px 0px 0px 1px rgba(0, 56, 108, 0.1);
}
.form-input::placeholder {
  color: #5E6678;
  transition: color 150ms ease-in-out;
  opacity: 0.9;
}
.form-input.invalid {
  box-shadow: none;
  border-color: #FF002F;
}
.form-input:hover::placeholder {
  color: #303546;
}
.form-input:focus::placeholder {
  color: #C1C7D0;
}

@property --submit-btn-opacity-1 {
syntax: '<number>';
initial-value: 0.12;
inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-color {
  syntax: '<color>';
  inherits: false;
}
.submit-btn {
  --submit-color: #121212;
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px var(--submit-color);
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out, 
    background-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out, 
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}
.submit-btn.enabled:hover {
  --submit-btn-opacity-1: 0.18;
  --submit-btn-opacity-2: 0.06;
}
.submit-btn.disabled {
  color: #A4ABB8;
  box-shadow: none;
  --submit-btn-opacity-1: 0;
  --submit-btn-opacity-2: 0;
  --submit-color: #F6F8FA;
}
</style>